import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import Button from '../ui/Button';
import type { CredentialGaps } from '../../services/sprayCredential';

interface Props {
  operatorName: string;
  gaps: CredentialGaps;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Shown when an assignment attempt returns 422 CREDENTIAL_GAPS. Lists what's
 * missing/expired and forces the creator to type a non-empty reason before
 * re-POSTing with `override_reason`.
 */
function CredentialOverrideDialog({ operatorName, gaps, submitting, onCancel, onConfirm }: Props) {
  const [reason, setReason] = useState('');

  const trimmed = reason.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <header className="px-5 py-4 border-b border-neutral-200 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Credential warning</h2>
              <p className="text-sm text-neutral-600">
                {operatorName} is missing or has expired required credentials.
              </p>
            </div>
          </div>
          <button onClick={onCancel} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-3">
          {gaps.missing.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-neutral-500 mb-1">Missing</p>
              <ul className="list-disc pl-5 text-sm text-neutral-700 space-y-0.5">
                {gaps.missing.map(m => (
                  <li key={`miss-${m.id}`}>{m.name}</li>
                ))}
              </ul>
            </div>
          )}
          {gaps.expired.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-neutral-500 mb-1">Expired</p>
              <ul className="list-disc pl-5 text-sm text-neutral-700 space-y-0.5">
                {gaps.expired.map(e => (
                  <li key={`exp-${e.id}`}>
                    {e.credential_type_name}
                    {e.expiry_date ? ` — expired ${e.expiry_date}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
            Assigning this operator will record an override and notify all organisation
            administrators and spray managers. A reason is required.
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Reason for override <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g. Operator's renewal has been submitted; pending issue of new certificate."
            />
          </div>
        </div>

        <footer className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button
            variant="danger"
            onClick={() => canSubmit && onConfirm(trimmed)}
            disabled={!canSubmit}
            isLoading={submitting}
          >
            Acknowledge and Assign
          </Button>
        </footer>
      </div>
    </div>
  );
}

export default CredentialOverrideDialog;
